<template>
    <div class="dashboard-container">
      <MetaManager
        title="Admin - Payouts"
        description="Payouts to local businesses"
      />
      <h1>Payouts</h1>
      <div class="controls-container">
        <div class="date-range-picker">
          <label for="startDate">Start Date:</label>
          <input type="date" id="startDate" v-model="startDate" @change="updateDateRange">
          <label for="endDate">End Date:</label>
          <input type="date" id="endDate" v-model="endDate" @change="updateDateRange">
          <button @click="updateDateRange" class="fetch-button">Fetch Data</button>
        </div>
        <div class="filter-container">
          <button @click="exportToExcel" class="export-button">
            <i class="fas fa-file-excel"></i> Export to Excel
          </button>
          <small>*Make sure all businesses are loaded before exporting</small>
        </div>
      </div>

      <div class="summary-boxes">
        <div class="summary-box">
          <h2>Total Businesses</h2>
          <p>{{ displayedBusinesses.length }}</p>
        </div>
        <div class="summary-box">
          <h2>Selected Period Payouts</h2>
          <p>${{ calculateTotalPayouts().toFixed(2) }}</p>
        </div>
        <div class="summary-box">
          <h2>Total Requested Payouts</h2>
          <p>${{ calculateTotalRequestedPayouts().toFixed(2) }}</p>
        </div>
        <div class="summary-box">
          <h2>Trio Tech Revenue</h2>
          <p>${{ calculateTotalRevenue().toFixed(2) }}</p>
        </div>
      </div>

      <div class="orders-container">
        <table class="desktop-table">
          <thead>
            <tr>
              <th>Business Name</th>
              <th>Period Payout ({{ dateRange }})</th>
              <th>Total Available Payout</th>
              <th>Payout Requested</th>
              <th>Trio Tech Revenue</th>
              <th>Business Rep</th>
              <th class="actions-column">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="business in displayedBusinesses" :key="business.business_name" class="order-row">
              <td>{{ business.business_name }}</td>
              <td>${{ business.current_month_payout.toFixed(2) }}</td>
              <td>${{ getTotalPayoutAmount(business).toFixed(2) }}</td>
              <td>{{ business.requested_payout ? 'Yes' : 'No' }}</td>
              <td>${{ business.trio_tech_cut.toFixed(2) }}</td>
              <td>{{ business.rep ? business.rep  : 'Trio Tech' }}</td>
              <td class="actions-column">
                <div class="action-buttons">
                  <button 
                    v-if="business.requested_payout" 
                    @click="confirmMarkAsPaid(business)" 
                    class="paid-button"
                  >
                    Paid
                  </button>
                  <button
                    v-if="hasPayoutHistory(business)"
                    @click="viewPayoutHistory(business)"
                    class="history-button"
                  >
                    History
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Confirmation Modal -->
      <div v-if="showConfirmModal" class="modal-overlay">
        <div class="modal-content">
          <h2>Confirm Payment</h2>
          <p>Are you sure you want to mark this payout as paid?</p>
          <p class="warning-text">A Fulle Payment will reset the payout to $0.00 and store teh payment in history</p>
          <p class="business-info">Business: {{ selectedBusiness?.business_name }}</p>
          <p class="amount-info">Amount: ${{ getTotalPayoutAmount(selectedBusiness).toFixed(2) }}</p>
          <div class="modal-actions">
            <button @click="showConfirmModal = false" class="cancel-button">Cancel</button>
            <button @click="processFullPayment" class="confirm-button">Confirm Full Payment</button>
            <button @click="openPartialPaymentModal" class="partial-button">Partial Payment</button>
          </div>
        </div>
      </div>

      <!-- Partial Payment Modal -->
      <div v-if="showPartialPaymentModal" class="modal-overlay">
        <div class="modal-content">
          <h2>Partial Payment</h2>
          <p class="business-info">Business: {{ selectedBusiness?.business_name }}</p>
          <p class="available-info">Available Amount: ${{ getTotalPayoutAmount(selectedBusiness).toFixed(2) }}</p>
          
          <div class="amount-input-container">
            <label for="partialAmount">Payment Amount:</label>
            <input 
              type="number" 
              id="partialAmount" 
              v-model.number="partialPaymentAmount" 
              :max="getTotalPayoutAmount(selectedBusiness)"
              min="0.01"
              step="0.01"
              class="amount-input"
              @input="validatePaymentAmount"
            >
          </div>
          
          <p v-if="partialPaymentAmount > getTotalPayoutAmount(selectedBusiness)" class="error-text">
            Amount cannot exceed available payout
          </p>
          
          <div class="modal-actions">
            <button @click="closePartialPaymentModal" class="cancel-button">Cancel</button>
            <button 
              @click="processPartialPayment" 
              class="confirm-button"
              :disabled="!isValidPaymentAmount"
            >
              Process Payment
            </button>
          </div>
        </div>
      </div>

      <!-- History Modal -->
      <div v-if="showHistoryModal" class="modal-overlay">
        <div class="modal-content history-modal">
          <h2>Payout History</h2>
          <p class="business-info">{{ selectedBusiness?.business_name }}</p>
          
          <div class="history-table-container">
            <table class="history-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(record, index) in payoutHistory" :key="index">
                  <td>{{ formatDate(record.date) }}</td>
                  <td>${{ record.amount.toFixed(2) }}</td>
                  <td>
                    <button @click="togglePayoutDetails(index)" class="details-button">
                      {{ expandedDetails.includes(index) ? 'Hide Details' : 'Show Details' }}
                    </button>
                    <div v-if="expandedDetails.includes(index)" class="payout-details">
                      <div v-if="record.payouts_snapshot && Array.isArray(record.payouts_snapshot)">
                        <h4>Monthly Breakdown</h4>
                        <table class="details-table">
                          <thead>
                            <tr>
                              <th>Month</th>
                              <th>Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(amount, i) in record.payouts_snapshot" :key="i" :class="{ 'has-value': parseFloat(amount) > 0 }">
                              <td>{{ getMonthName(i) }}</td>
                              <td>${{ parseFloat(amount).toFixed(2) }}</td>
                            </tr>
                          </tbody>
                          <tfoot>
                            <tr>
                              <td><strong>Total</strong></td>
                              <td><strong>${{ calculateTotal(record.payouts_snapshot).toFixed(2) }}</strong></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                      <div v-else>
                        <p>No detailed breakdown available</p>
                      </div>
                    </div>
                  </td>
                </tr>
                <tr v-if="payoutHistory.length === 0">
                  <td colspan="3">No payment history available</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div class="modal-actions">
            <button @click="showHistoryModal = false" class="cancel-button">Close</button>
          </div>
        </div>
      </div>
    </div>
  </template>

  <script>
  import axios from 'axios';
  import MetaManager from '../../components/MetaManager.vue';

  export default {
    name: 'DashboardAdminPayout',
    components: {
      MetaManager,
    },
    data() {
      return {
        businesses: [],
        startDate: '',
        endDate: '',
        dateRange: '',
        showConfirmModal: false,
        showHistoryModal: false,
        showPartialPaymentModal: false,
        selectedBusiness: null,
        partialPaymentAmount: 0,
        payoutHistory: [],
        expandedDetails: [],
      };
    },
    computed: {
      displayedBusinesses() {
        return this.businesses;
      },
      isValidPaymentAmount() {
        return this.partialPaymentAmount > 0 && 
               this.partialPaymentAmount <= this.getTotalPayoutAmount(this.selectedBusiness);
      }
    },
    mounted() {
      this.fetchPayoutData();
      this.calculateDateRange();
    },
    methods: {
      async fetchPayoutData(startDate = this.startDate, endDate = this.endDate) {
        try {
          const response = await axios.get(`${process.env.VUE_APP_API_URL}/api/business-sales/payouts`, {
            params: { startDate, endDate },
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          });
          this.businesses = response.data;
          if (response.data.length > 0 && response.data[0].date_range) {
            const { startDate, endDate } = response.data[0].date_range;
            this.dateRange = `${startDate} to ${endDate}`;
          }
        } catch (error) {
          console.error('Error fetching payout data:', error);
        }
      },
      calculateDateRange() {
        const date = new Date();
        const firstDay = new Date(date.getFullYear(), date.getMonth(), 1).toISOString().split('T')[0];
        const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).toISOString().split('T')[0];
        this.startDate = firstDay;
        this.endDate = lastDay;
        this.dateRange = `${firstDay} - ${lastDay}`;
      },
      updateDateRange() {
        if (this.startDate && this.endDate) {
          this.dateRange = `${this.startDate} - ${this.endDate}`;
          this.fetchPayoutData(this.startDate, this.endDate);
        }
      },
      async markAsPaid(businessId) {
        try {
          await axios.post(
            `${process.env.VUE_APP_API_URL}/api/business-sales/reset-payout`,
            { business_id: businessId },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
              },
            }
          );
          const businessRecord = this.businesses.find(b => b.business_id === businessId);
          if (businessRecord) {
            businessRecord.requested_payout = false;
          }
        } catch (error) {
          console.error('Error marking payout as paid:', error);
        }
      },
      calculateTotalPayouts() {
        return this.displayedBusinesses.reduce((sum, business) => {
          return sum + (business.current_month_payout || 0);
        }, 0);
      },
      calculateTotalRequestedPayouts() {
        let total = 0;
        for (const biz of this.displayedBusinesses) {
          if (biz.requested_payout) {
            total += this.getTotalPayoutAmount(biz);
          }
        }
        return total;
      },
      calculateTotalRevenue() {
        return this.displayedBusinesses.reduce((sum, business) => {
          return sum + (business.trio_tech_cut || 0);
        }, 0);
      },
      getTotalPayoutAmount(business) {
        if (!business.payouts) return 0;
        return business.payouts.reduce((total, amount) => {
          return total + (parseFloat(amount) || 0);
        }, 0);
      },
      exportToExcel() {
        const headers = [
          'Business Name', 
          `Period Payout (${this.dateRange})`, 
          'Total Available Payout',
          'Payout Requested', 
          'Trio Tech Revenue', 
          'Business Rep'
        ];
        const escapeCSV = (field) => {
          if (field === null || field === undefined) {
            return '""';
          }
          const stringField = String(field);
          return `"${stringField.replace(/"/g, '""')}"`;
        };
        const rows = this.displayedBusinesses.map(business => [
          escapeCSV(business.business_name),
          escapeCSV('$' + business.current_month_payout.toFixed(2)),
          escapeCSV('$' + this.getTotalPayoutAmount(business).toFixed(2)),
          escapeCSV(business.requested_payout ? 'Yes' : 'No'),
          escapeCSV('$' + business.trio_tech_cut.toFixed(2)),
          escapeCSV(business.rep || 'Trio Tech')
        ]);
        const headerRow = headers.map(header => escapeCSV(header)).join(',');
        const dataRows = rows.map(row => row.join(',')).join('\n');
        const csvContent = headerRow + '\n' + dataRows;
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Payouts_${this.dateRange.replace(/\s/g, '_')}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      },
      confirmMarkAsPaid(business) {
        this.selectedBusiness = business;
        this.showConfirmModal = true;
      },
      
      openPartialPaymentModal() {
        this.showConfirmModal = false;
        this.partialPaymentAmount = 0;
        this.showPartialPaymentModal = true;
      },
      
      closePartialPaymentModal() {
        this.showPartialPaymentModal = false;
        this.partialPaymentAmount = 0;
        this.selectedBusiness = null;
      },
      
      validatePaymentAmount() {
        if (this.partialPaymentAmount < 0) {
          this.partialPaymentAmount = 0;
        }
        const maxAmount = this.getTotalPayoutAmount(this.selectedBusiness);
        if (this.partialPaymentAmount > maxAmount) {
          this.partialPaymentAmount = maxAmount;
        }
      },

      async processFullPayment() {
        if (!this.selectedBusiness) return;
        try {
          await axios.post(
            `${process.env.VUE_APP_API_URL}/api/business-sales/reset-payout`,
            { 
              business_id: this.selectedBusiness.business_id,
              payment_type: 'full'
            },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
              },
            }
          );
          const business = this.businesses.find(b => b.business_id === this.selectedBusiness.business_id);
          if (business) {
            business.requested_payout = false;
            if (Array.isArray(business.payouts)) {
              business.payouts = business.payouts.map(() => 0);
            }
          }
          this.showConfirmModal = false;
          this.selectedBusiness = null;
        } catch (error) {
          console.error('Error marking payout as paid:', error);
        }
      },

      async processPartialPayment() {
        if (!this.selectedBusiness || !this.isValidPaymentAmount) return;
        
        try {
          await axios.post(
            `${process.env.VUE_APP_API_URL}/api/business-sales/partial-payout`,
            { 
              business_id: this.selectedBusiness.business_id,
              payment_amount: this.partialPaymentAmount
            },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
              },
            }
          );
          
          // Refresh the data to show updated amounts
          await this.fetchPayoutData();
          
          this.closePartialPaymentModal();
        } catch (error) {
          console.error('Error processing partial payment:', error);
        }
      },

      async processPayment() {
        // This method is now replaced by processFullPayment
        return this.processFullPayment();
      },

      hasPayoutHistory(business) {
        if (!business) return false;
        if (business.has_payout_history === true) {
          return true;
        }
        if (business.payouts && Array.isArray(business.payouts)) {
          const hasNonZeroPayouts = business.payouts.some(amount => parseFloat(amount) > 0);
          if (hasNonZeroPayouts) {
            return true;
          }
        }
        return business.requested_payout === true;
      },
      async viewPayoutHistory(business) {
        this.selectedBusiness = business;
        this.payoutHistory = [];
        try {
          const response = await axios.get(
            `${process.env.VUE_APP_API_URL}/api/business-sales/payout-history/${business.business_id}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
              },
            }
          );
          this.payoutHistory = response.data || [];
          this.showHistoryModal = true;
        } catch (error) {
          console.error('Error fetching payout history:', error);
        }
      },
      formatDate(dateString) {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric', 
          hour: '2-digit',
          minute: '2-digit'
        });
      },
      togglePayoutDetails(index) {
        const position = this.expandedDetails.indexOf(index);
        if (position > -1) {
          this.expandedDetails.splice(position, 1);
        } else {
          this.expandedDetails.push(index);
        }
      },
      getMonthName(index) {
        const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                       'July', 'August', 'September', 'October', 'November', 'December'];
        return months[index];
      },
      calculateTotal(payouts) {
        if (!Array.isArray(payouts)) return 0;
        return payouts.reduce((sum, amount) => sum + (parseFloat(amount) || 0), 0);
      }
    }
  };
  </script>

  <style scoped>
  .dashboard-container {
    padding: 20px;
    font-family: 'Arial', sans-serif;
    margin-left: 300px;
    background-color: #f9f9f9;
  }
  h1 {
    text-align: center;
    margin-bottom: 20px;
    color: #333;
    font-weight: 700;
  }
  .controls-container {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    margin-bottom: 25px;
    background-color: #fff;
    padding: 15px;
    border-radius: 8px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }
  .date-range-picker, .filter-container {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }
  .date-range-picker label, .filter-container label {
    font-weight: 600;
    color: #4C6B30;
  }
  .date-range-picker input, .filter-container input {
    padding: 8px 12px;
    border-radius: 4px;
    border: 1px solid #ddd;
    background-color: #f5f5f5;
    transition: border-color 0.2s;
  }
  .date-range-picker input:focus, .filter-container input:focus {
    border-color: #4C6B30;
    outline: none;
  }
  .fetch-button, .export-button {
    padding: 8px 15px;
    background-color: #4C6B30;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.2s;
    display: flex;
    align-items: center;
    gap: 5px;
  }
  .fetch-button:hover, .export-button:hover {
    background-color: #6E9F49;
  }
  .summary-boxes {
    display: flex;
    justify-content: space-between;
    margin-bottom: 25px;
    flex-wrap: wrap;
    gap: 15px;
  }
  .summary-box {
    flex: 1;
    min-width: 200px;
    background: #fff;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    text-align: center;
    transition: transform 0.2s, box-shadow 0.2s;
  }
  .summary-box:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  .summary-box h2 {
    font-size: 1.1em;
    margin-bottom: 10px;
    color: #4C6B30;
  }
  .summary-box p {
    font-size: 1.8em;
    font-weight: 700;
    color: #333;
  }
  .orders-container {
    background: #fff;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    overflow-x: auto;
  }
  .desktop-table {
    width: 100%;
    border-collapse: collapse;
  }
  .desktop-table th, .desktop-table td {
    padding: 12px;
    border-bottom: 1px solid #eee;
    text-align: left;
  }
  .desktop-table th {
    background: #f5f5f5;
    font-weight: 600;
    color: #4C6B30;
    position: relative;
  }
  .desktop-table tr:hover {
    background-color: #f9f9f9;
  }
  .order-row {
    cursor: pointer;
    transition: background-color 0.2s;
  }
  .filter-container small {
    color: #666;
    font-style: italic;
  }
  @media(max-width: 1024px){
    .dashboard-container{
      margin-left: 0;
    }
    .controls-container {
      flex-direction: column;
      gap: 15px;
    }
    .date-range-picker, .filter-container {
      width: 100%;
    }
  }
  @media (max-width: 768px) {
    .dashboard-container {
      margin-left: 0;
      padding: 10px;
    }
    .summary-box {
      min-width: 100%;
    }
    .controls-container {
      padding: 10px;
    }
    .desktop-table {
      font-size: 0.9em;
    }
  }
  @media (max-width: 480px) {
    .controls-container, .summary-boxes {
      padding: 10px;
    }
    .desktop-table th, .desktop-table td {
      padding: 8px 4px;
      font-size: 0.85em;
    }
  }
  @media (max-width: 1200px) {
    .desktop-table th, .desktop-table td {
      padding: 8px 6px;
      font-size: 0.9em;
    }
  }
  /* Modal styles */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }
  .modal-content {
    background-color: white;
    border-radius: 8px;
    padding: 20px;
    width: 90%;
    max-width: 500px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  }
  .history-modal {
    max-width: 700px;
  }
  .modal-content h2 {
    margin-top: 0;
    color: #4C6B30;
  }
  .warning-text {
    color: #e74c3c;
    font-weight: bold;
  }
  .business-info, .amount-info {
    font-weight: bold;
    margin: 10px 0;
  }
  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 20px;
  }
  .cancel-button, .confirm-button {
    padding: 8px 16px;
    border-radius: 4px;
    border: none;
    cursor: pointer;
    font-weight: bold;
  }
  .cancel-button {
    background-color: #e74c3c;
    color: white;
  }
  .confirm-button {
    background-color: #4C6B30;
    color: white;
  }
  .history-button {
    padding: 8px 15px;
    background-color: #3498db;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-weight: bold;
    margin-left: 5px;
  }
  .history-table-container {
    max-height: 400px;
    overflow-y: auto;
    margin: 15px 0;
  }
  .history-table {
    width: 100%;
    border-collapse: collapse;
  }
  .history-table th, .history-table td {
    padding: 10px;
    border-bottom: 1px solid #eee;
    text-align: left;
  }
  .history-table th {
    background-color: #f5f5f5;
    font-weight: bold;
    color: #4C6B30;
  }
  .details-button {
    background-color: #6E9F49;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 5px 8px;
    cursor: pointer;
    font-size: 0.8rem;
  }
  .payout-details {
    margin-top: 8px;
    padding: 12px;
    background-color: #f9f9f9;
    border-radius: 4px;
    font-size: 0.8rem;
    max-height: 300px;
    overflow-y: auto;
    border: 1px solid #eee;
  }
  .details-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.85rem;
    margin-top: 10px;
    margin-bottom: 5px;
  }
  .details-table th, 
  .details-table td {
    padding: 6px 10px;
    text-align: left;
    border-bottom: 1px solid #ddd;
  }
  .details-table th {
    background-color: #f0f0f0;
    font-weight: 600;
  }
  .details-table tfoot {
    font-weight: bold;
    background-color: #f5f5f5;
  }
  .details-table .has-value {
    background-color: #e8f4e5;
  }
  .payout-details h4 {
    margin: 0 0 8px 0;
    font-size: 0.9rem;
    color: #4C6B30;
  }

  .actions-column {
    min-width: 150px; /* Ensures enough space for buttons */
  }

  .action-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .paid-button, .history-button {
    padding: 8px 15px;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.2s;
    font-weight: bold;
    flex: 0 0 auto;
    white-space: nowrap;
    min-width: 80px;
    text-align: center;
  }

  .paid-button {
    background-color: #4C6B30;
  }

  .paid-button:hover {
    background-color: #6E9F49;
  }

  .history-button {
    background-color: #3498db;
    margin-left: 0; /* Remove the left margin */
  }

  .history-button:hover {
    background-color: #2980b9;
  }

  /* For smaller screens, stack the buttons vertically */
  @media (max-width: 992px) {
    .action-buttons {
      flex-direction: column;
    }
  }

  /* Partial Payment Modal styles */
  .amount-input-container {
    margin: 15px 0;
  }

  .amount-input-container label {
    display: block;
    margin-bottom: 5px;
    font-weight: bold;
    color: #4C6B30;
  }

  .amount-input {
    width: 95%;
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
  }

  .amount-input:focus {
    border-color: #4C6B30;
    outline: none;
  }

  .available-info {
    color: #666;
    font-weight: bold;
    margin: 10px 0;
  }

  .error-text {
    color: #e74c3c;
    font-size: 0.9rem;
    margin: 5px 0;
  }

  .partial-button {
    background-color: #f39c12;
    color: white;
    padding: 8px 16px;
    border-radius: 4px;
    border: none;
    cursor: pointer;
    font-weight: bold;
  }

  .partial-button:hover {
    background-color: #e67e22;
  }

  .confirm-button:disabled {
    background-color: #bdc3c7;
    cursor: not-allowed;
  }

  .confirm-button:disabled:hover {
    background-color: #bdc3c7;
  }
  </style>